import { axiosArisChatInstance } from "../AxiosConfiguration";

/**
 * Explore feed — read directly from aris-ai.
 *
 * These used to go through core-lambda's explore_module, which was a byte-for-byte
 * pass-through: it forwarded the member's own Authorization header, relayed the body
 * unparsed and the status verbatim, and shaped nothing. So it was not an auth
 * boundary or a composition layer — just a second network leg, and one paid for by
 * the millisecond, since core-lambda is a Lambda that sits blocked on the upstream
 * call for its whole duration.
 *
 * The browser already reaches aris-ai directly for chat, dialogue detail and the
 * voice websocket, so nothing new is exposed by doing the same here.
 *
 * The response contract belongs to aris-ai (`core/explore/contracts.py`).
 */

const unwrap = (res) => {
  const data = res?.data;
  if (data && typeof data === "object") return data.payload ?? data;
  return data;
};

/**
 * The v26.5 feed sections, in the order aris-ai emits them (guide §6.1). Order is
 * the backend's, not ours: render in array order and never sort.
 *
 * Retired in v26.5 and never sent: SEARCH, SUGGESTED_CONVERSATIONS,
 * CURRENT_FOCUS, SAVED_FOR_LATER, SPOTLIGHT. NEXT_BEST_STEPS and
 * FEATURED_EXPERTS were renamed with new semantics, not aliased.
 */
export const SECTION_TYPE = {
  RECOMMENDED_FOR_YOU: "RECOMMENDED_FOR_YOU",
  ACTIVE_DIALOGUES: "ACTIVE_DIALOGUES",
  PERSONAL_DIALOGUES: "PERSONAL_DIALOGUES",
  LIFE_AREAS: "LIFE_AREAS",
  ORGANIZATION: "ORGANIZATION",
  EXPERT_DIALOGUES: "EXPERT_DIALOGUES",
  HBR_DIALOGUES: "HBR_DIALOGUES",
  SUGGESTED_ACTIONS: "SUGGESTED_ACTIONS",
  TALK_WITH_ARIS: "TALK_WITH_ARIS",
};

/**
 * At most one badge per card, resolved server-side. `PRIMARY_FOCUS` stays in
 * the enum but is never emitted any more (item 54).
 */
export const CARD_BADGE = {
  SAVED: "SAVED",
  PREVIOUSLY_EXPERIENCED: "PREVIOUSLY_EXPERIENCED",
};

export const FOUNDATION_STATUS = {
  PRE_ACTIVATION: "PRE_ACTIVATION",
};

/**
 * `GET /ARIS/v4/explore` → { sections, foundation_status, snapshot_generated_at }.
 *
 * A member who has not activated yet gets `{ sections: [], foundation_status:
 * "PRE_ACTIVATION" }` (aris-ai `explore_feed_service.build`) — that is the
 * signal for the "preparing" view.
 */
export const getExploreFeed = async () => {
  const res = await axiosArisChatInstance.get("/ARIS/v4/explore");
  return unwrap(res);
};

/**
 * True when Aris has nothing to show yet, so the member sees the preparing view.
 *
 * Keyed on "no sections" rather than on an exhaustive list of status values:
 * PRE_ACTIVATION is the only status literal in aris-ai today, and a feed with
 * no sections has nothing to render regardless of what the status says.
 */
export const isPreparing = (feed) =>
  !feed || !Array.isArray(feed.sections) || feed.sections.length === 0;

export const PREPARATION_STATUS = {
  // Before activation completes (Explore gaps E9): the member finishes
  // activation first; the Preparing page starts after it.
  NOT_STARTED: "NOT_STARTED",
  PREPARING: "PREPARING",
  READY: "READY",
  FAILED: "FAILED",
};


/**
 * Whether Explore shows the Preparing page instead of the feed.
 *
 * - No sections → always Preparing (the original rule).
 * - `feed.preparation_status` (proposed, Explore gaps E1) wins when present.
 * - Otherwise `/explore/preparation` holds the member on Preparing while it
 *   says PREPARING **and a run is live** (`started_at` known, before
 *   `estimated_ready_by`). A member whose status is stuck — e.g. no ACTIVE
 *   Mission, so step 1 never ticks — must not be locked out of a feed that
 *   has content, so past the 24-hour window, or with no run at all, the
 *   sections decide.
 */
export const shouldShowPreparing = (feed, prep, now = Date.now()) => {
  if (isPreparing(feed)) return true;
  const feedStatus = feed?.preparation_status;
  if (feedStatus) return feedStatus === PREPARATION_STATUS.PREPARING;
  if (prep?.status !== PREPARATION_STATUS.PREPARING) return false;
  const started = Date.parse(prep.started_at);
  const until = Date.parse(prep.estimated_ready_by);
  if (Number.isNaN(started) || Number.isNaN(until)) return false;
  return now >= started && now < until;
};

/** Poll interval while preparing: the server's hint (E6) or 15 s. */
export const preparationPollMs = (prep) => {
  const s = Number(prep?.retry_after_seconds);
  return Number.isFinite(s) && s >= 5 ? s * 1000 : 15000;
};

/**
 * `GET /ARIS/v4/explore/preparation` — the Preparing checklist (guide §6.7,
 * WEB_INTEGRATION WP-W12): `{status, started_at?, estimated_ready_by?,
 * steps[{key, order, state, tracked, title_key, subtitle_key}]}`.
 *
 * Only used to fill in the checklist once the preparing view is showing; the
 * view itself is still keyed on the feed having no sections.
 */
export const getPreparation = async () => {
  const res = await axiosArisChatInstance.get("/ARIS/v4/explore/preparation");
  return unwrap(res);
};

/**
 * `GET /ARIS/v4/explore/life-areas` — every Life Area with its copy, colour and
 * icon, in display order (aris-core's Life Area registry).
 */
export const getLifeAreas = async () => {
  const res = await axiosArisChatInstance.get("/ARIS/v4/explore/life-areas");
  return unwrap(res)?.areas || [];
};

/** `GET /ARIS/v4/explore/life-areas/{key}` — the per-area feed (4 sections). */
export const getLifeAreaFeed = async (displayAreaKey) => {
  const res = await axiosArisChatInstance.get(
    `/ARIS/v4/explore/life-areas/${encodeURIComponent(displayAreaKey)}`
  );
  return unwrap(res);
};

/**
 * `GET /ARIS/v4/explore/self-knowledge` — the specialised Self-Knowledge area
 * (aris-ai `core/explore/self_knowledge_area.py`): `{ display_area,
 * specialised, foundation_complete, hero, sections }` where sections are keyed
 * by `key` (CONTINUE_YOUR_DISCOVERIES / SELF_KNOWLEDGE_COMPLETE /
 * EXPLORE_YOUR_PERSONAL_MODEL / RECOMMENDED_FOR_YOU / TALK_WITH_ARIS).
 */
export const getSelfKnowledge = async () => {
  const res = await axiosArisChatInstance.get("/ARIS/v4/explore/self-knowledge");
  return unwrap(res);
};

/**
 * `GET /ARIS/v4/explore/my-dialogues` — the Personal Dialogue Library (DCI),
 * raw: `{filter, count, empty, dialogues[]}`. Each row carries `dialogue_id`,
 * `slug`, `title`, `source_line?`, `created_at`, `status` and `href`
 * (WEB_INTEGRATION WP-W7). `filter` is deprecated and not sent.
 *
 * Needs `dci_v26` for the member: with it off the route 404s, which means
 * "not available", not "failed" — so that case resolves to `null` and every
 * other error still throws.
 */
export const getMyDialogues = async (q) => {
  try {
    // DCI v27 (aris-ai plan 53): archived rows come back in `archived`, and `q`
    // searches the member's own Personal Dialogues (archived included).
    const params = q && q.trim() ? { q: q.trim() } : undefined;
    const res = await axiosArisChatInstance.get("/ARIS/v4/explore/my-dialogues", {
      params,
    });
    return unwrap(res);
  } catch (e) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
};

/**
 * `GET /ARIS/v4/explore/search` — Explore Search v2 (aris-ai plan 47 §4).
 *
 * `{ query, adequate, dialogues[], results[], life_areas[], collections[],
 * related_searches[], counts, talk_it_through? }`. `dialogues` are curated results in
 * AGS order — render in array order and never re-sort. Since DCI v27 (aris-ai plan
 * 53) the member's own Personal Dialogues are a separate group:
 * `personal_dialogues: {items (≤3), total, see_all_href}`. `family` narrows to one Collection
 * (the `collections[].href` drill-in); `limit` is "See All".
 */
export const SEARCH_MIN_CHARS = 3;

export const searchExplore = async (query, { limit, family, signal } = {}) => {
  const params = { q: query };
  if (limit) params.limit = limit;
  if (family) params.family = family;
  const res = await axiosArisChatInstance.get("/ARIS/v4/explore/search", {
    params,
    signal,
  });
  return unwrap(res);
};

/** Sections keyed by type, for renderers that want to look one up directly. */
export const sectionsByType = (feed) => {
  const out = {};
  (feed?.sections || []).forEach((section) => {
    if (section?.type) out[section.type] = section;
  });
  return out;
};

/**
 * The Explore feed is read fresh on every mount, so there is no cache to
 * drop; this tells a mounted Explore page (another tab of the app, or a
 * future cached feed) that a Dialogue changed state and the Active rail is
 * stale. Lifecycle moves call it after they succeed.
 */
export const EXPLORE_FEED_INVALIDATED = "aris:explore-feed-invalidated";

export const invalidateExploreFeed = () => {
  try {
    window.dispatchEvent(new Event(EXPLORE_FEED_INVALIDATED));
  } catch (_) {
    /* no window (SSR / tests without jsdom) — nothing is listening */
  }
};

/**
 * `PATCH /ARIS/v4/personal-dialogues/{id}` — edit a Personal Dialogue after save (DCI
 * v27 Part I §3). Send only changed fields; `expected_content_version` is required for
 * anything but the title. Returns the Overview's `personal` block.
 */
export const updatePersonalDialogue = async (dialogueId, fields) => {
  const res = await axiosArisChatInstance.patch(
    `/ARIS/v4/personal-dialogues/${encodeURIComponent(dialogueId)}`,
    fields
  );
  return unwrap(res);
};
